import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DobbyDemoPage from "../DobbyDemoPage";
import { DataTableComponent } from "../../components/EmailDataTableComponent/DataTableComponent";
import type { DataTableColumn } from "../../components/EmailDataTableComponent/DataTableComponent";
import type { ChatSource } from "@services/chat/types";
import type { Reminder } from "@services/demo/msoffice/types";
import { apiClient } from "../../utils/apiClient";
import { ShipmentDetailDrawer } from "./ShipmentDetailDrawer";

// ── Types ──

interface DemoShipment {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  tags: string[];
  properties: Array<{ name: string; value: string; status?: string }>;
  errors: any[];
  flaggedAsInvalid: boolean;
  mergedWithShipmentId: string | null;
  adminNotes: string | null;
  lastErrorTimestamp: number | null;
  assignedToIds: string[];
  totalReminders: number;
  unresolvedReminders: number;
}

// ── Helpers ──

const getPropertyValue = (
  shipment: DemoShipment,
  propertyName: string,
): string => {
  const matching = shipment.properties.filter((p) => p.name === propertyName);
  if (matching.length === 0) return "";
  return Array.from(new Set(matching.map((p) => p.value))).join(", ");
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "-";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

// ── Table columns ──

const demoTableColumns: DataTableColumn<DemoShipment>[] = [
  {
    columnName: "Reminders",
    renderFn: (s) => {
      const count = s.unresolvedReminders || 0;
      return count > 0 ? (
        <Chip
          icon={<NotificationsActiveIcon sx={{ fontSize: 14 }} />}
          label={count}
          size="small"
          color="warning"
          sx={{ height: 22, fontSize: "0.75rem" }}
        />
      ) : (
        "-"
      );
    },
    flex: 0.7,
    visibleByDefault: true,
  },
  {
    columnName: "Booking",
    renderFn: (s) => getPropertyValue(s, "booking_number"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
  },
  {
    columnName: "Shipper",
    renderFn: (s) => getPropertyValue(s, "shipper"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
  },
  {
    columnName: "Consignee",
    renderFn: (s) => getPropertyValue(s, "consignee"),
    clipText: true,
    flex: 1.5,
    visibleByDefault: true,
  },
  {
    columnName: "Port of Loading",
    renderFn: (s) => getPropertyValue(s, "port_of_loading"),
    clipText: true,
    flex: 1.2,
    visibleByDefault: true,
  },
  {
    columnName: "Port of Discharge",
    renderFn: (s) => getPropertyValue(s, "port_of_discharge"),
    clipText: true,
    flex: 1.2,
    visibleByDefault: true,
  },
  {
    columnName: "Container",
    renderFn: (s) => getPropertyValue(s, "container_number"),
    clipText: true,
    flex: 1.3,
    visibleByDefault: true,
  },
  {
    columnName: "Vessel",
    renderFn: (s) => getPropertyValue(s, "vessel_name"),
    clipText: true,
    flex: 1.3,
    visibleByDefault: true,
  },
  {
    columnName: "Shipping Line",
    renderFn: (s) => getPropertyValue(s, "shipping_line"),
    clipText: true,
    flex: 1,
    visibleByDefault: true,
  },
  {
    columnName: "ETD",
    renderFn: (s) => formatDate(getPropertyValue(s, "etd")),
    clipText: true,
    flex: 1,
    visibleByDefault: true,
  },
  {
    columnName: "ETA",
    renderFn: (s) => formatDate(getPropertyValue(s, "eta")),
    clipText: true,
    flex: 1,
    visibleByDefault: true,
  },
  {
    columnName: "House BL",
    renderFn: (s) => getPropertyValue(s, "house_bill_of_lading"),
    clipText: true,
    flex: 1.2,
    visibleByDefault: false,
  },
  {
    columnName: "Master BL",
    renderFn: (s) => getPropertyValue(s, "master_bill_of_lading"),
    clipText: true,
    flex: 1.2,
    visibleByDefault: false,
  },
];

// ── Main component ──

export const DemoShipmentFeedPage: React.FC = () => {
  const [shipments, setShipments] = useState<DemoShipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedShipment, setSelectedShipment] = useState<DemoShipment | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [allReminders, setAllReminders] = useState<Reminder[]>([]);
  const [defaultChatAnswer, setDefaultChatAnswer] = useState<{
    dobbyResponse: string;
    sources?: ChatSource[];
  } | null>(null);

  // Fetch shipments on mount
  useEffect(() => {
    const fetchShipments = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get<{
          shipments: DemoShipment[];
          total: number;
        }>("/demo-shipment-feed/shipments");
        setShipments(response.shipments);
      } catch (error) {
        console.error("Error fetching demo shipments:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchShipments();
  }, []);

  // Fetch reminders and chat answer on mount
  useEffect(() => {
    const fetchReminders = async () => {
      try {
        const response = await apiClient.get<{ data: Reminder[] }>(
          "/demo-shipment-feed/reminders",
        );
        setAllReminders(response.data);
      } catch (error) {
        console.error("Error fetching demo reminders:", error);
      }
    };

    const fetchChatAnswer = async () => {
      try {
        const response = await apiClient.get<{
          dobbyResponse: string;
          sources?: ChatSource[];
        }>("/demo-shipment-feed/chat-answer");
        setDefaultChatAnswer(response);
      } catch (error) {
        console.error("Error fetching demo chat answer:", error);
      }
    };

    fetchReminders();
    fetchChatAnswer();
  }, []);

  // Filter shipments by search
  const filteredShipments = useMemo(() => {
    if (!searchQuery.trim()) return shipments;
    const q = searchQuery.toLowerCase();
    return shipments.filter((s) => {
      const searchable = [s.name, ...s.properties.map((p) => p.value)]
        .join(" ")
        .toLowerCase();
      return searchable.includes(q);
    });
  }, [shipments, searchQuery]);

  const handleRowClick = useCallback((shipment: DemoShipment) => {
    setSelectedShipment(shipment);
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  return (
    <DobbyDemoPage fullHeight>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Filter bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 2,
            py: 1,
            borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <TextField
            size="small"
            placeholder="Search shipments..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <Typography variant="body2" color="text.secondary">
            {filteredShipments.length} shipments
          </Typography>
        </Box>

        {/* Data table */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <DataTableComponent
            rows={filteredShipments}
            columns={demoTableColumns}
            keyField="id"
            onRowClick={handleRowClick}
            selectedRowKey={selectedShipment?.id}
            stickyHeader
            showColumnVisibilityToggle
            isLoading={loading}
            density="compact"
          />
        </Box>
      </Box>

      <ShipmentDetailDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        shipment={selectedShipment}
        allReminders={allReminders}
        defaultChatAnswer={defaultChatAnswer}
      />
    </DobbyDemoPage>
  );
};
